import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Eye, AlertTriangle, CheckCircle, XCircle, Clock, DollarSign, Navigation } from 'lucide-react';
import { StatsCard } from '../components/StatsCard';
import { DataTable } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
const deliveries = [{
  id: 'FAMO-9281',
  customer: 'Alice Smith',
  rider: 'John Doe',
  pickup: '123 Main St',
  dropoff: 'LAX Airport',
  status: 'active',
  fare: '₦45.00',
  distance: '12.4 km',
  time: 'Now'
}, {
  id: 'FAMO-9280',
  customer: 'Bob Johnson',
  rider: 'Mike Ross',
  pickup: 'Central Park',
  dropoff: 'Times Square',
  status: 'completed',
  fare: '₦15.50',
  distance: '2.1 km',
  time: '10:45 AM'
}, {
  id: 'FAMO-9279',
  customer: 'Charlie Brown',
  rider: 'Sarah Connor',
  pickup: 'Union Station',
  dropoff: 'Convention Center',
  status: 'cancelled',
  fare: '₦0.00',
  distance: '0 km',
  time: '10:30 AM'
}, {
  id: 'FAMO-9278',
  customer: 'David Wilson',
  rider: 'Bruce Wayne',
  pickup: 'Wayne Manor',
  dropoff: 'GCPD',
  status: 'completed',
  fare: '₦28.00',
  distance: '8.5 km',
  time: '10:15 AM'
}, {
  id: 'FAMO-9277',
  customer: 'Eve Davis',
  rider: 'Clark Kent',
  pickup: 'Daily Planet',
  dropoff: 'Metropolis Park',
  status: 'in_transit',
  fare: '₦12.00',
  distance: '3.2 km',
  time: 'Now'
}, {
  id: 'FAMO-9276',
  customer: 'Frank Miller',
  rider: 'Diana Prince',
  pickup: 'Museum',
  dropoff: 'Embassy',
  status: 'picked_up',
  fare: '₦22.00',
  distance: '5.6 km',
  time: '09:50 AM'
}, {
  id: 'FAMO-9275',
  customer: 'Grace Lee',
  rider: 'Barry Allen',
  pickup: 'CC Jitters',
  dropoff: 'STAR Labs',
  status: 'failed',
  fare: '₦8.50',
  distance: '1.2 km',
  time: '09:45 AM'
}, {
  id: 'FAMO-9274',
  customer: 'Henry Ford',
  rider: 'Arthur Curry',
  pickup: 'Lighthouse',
  dropoff: 'Docks',
  status: 'disputed',
  fare: '₦5.00',
  distance: '0.5 km',
  time: '09:30 AM'
}];
const rescheduleRequests = [{
  id: 'REQ-101',
  deliveryId: 'FAMO-9300',
  originalDate: 'Today, 2:00 PM',
  requestedDate: 'Tomorrow, 10:00 AM',
  customer: 'Alice Smith',
  reason: 'Unexpected meeting conflict'
}, {
  id: 'REQ-102',
  deliveryId: 'FAMO-9301',
  originalDate: 'Today, 4:30 PM',
  requestedDate: 'Today, 6:00 PM',
  customer: 'Bob Johnson',
  reason: 'Running late from work'
}, {
  id: 'REQ-103',
  deliveryId: 'FAMO-9302',
  originalDate: 'Tomorrow, 9:00 AM',
  requestedDate: 'Tomorrow, 1:00 PM',
  customer: 'Charlie Brown',
  reason: 'Flight delayed'
}, {
  id: 'REQ-104',
  deliveryId: 'FAMO-9303',
  originalDate: 'Friday, 12:00 PM',
  requestedDate: 'Saturday, 12:00 PM',
  customer: 'David Wilson',
  reason: 'Change of plans'
}];
export function DeliveryOversight() {
  const [activeTab, setActiveTab] = useState('all');
  const columns = [{
    header: 'Delivery ID',
    accessor: 'id',
    className: 'font-mono text-xs'
  }, {
    header: 'Customer',
    accessor: 'customer'
  }, {
    header: 'Rider',
    accessor: 'rider'
  }, {
    header: 'Route',
    accessor: (row: any) => <div className="flex flex-col gap-1 text-xs max-w-[200px]" data-id="element-305">
          <div className="flex items-center gap-1 text-slate-600 truncate" data-id="element-306">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" data-id="element-307" />
            {row.pickup}
          </div>
          <div className="flex items-center gap-1 text-slate-600 truncate" data-id="element-308">
            <div className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" data-id="element-309" />
            {row.dropoff}
          </div>
        </div>
  }, {
    header: 'Status',
    accessor: (row: any) => <div className="flex items-center gap-2" data-id="element-310">
          <StatusBadge status={row.status} data-id="element-311" />
          {(row.status === 'active' || row.status === 'in_transit' || row.status === 'picked_up') && <span className="relative flex h-2 w-2" data-id="element-312">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-313"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" data-id="element-314"></span>
            </span>}
        </div>
  }, {
    header: 'Fare',
    accessor: 'fare',
    className: 'font-medium'
  }, {
    header: 'Distance',
    accessor: 'distance'
  }, {
    header: 'Time',
    accessor: 'time'
  }];
  const filteredData = activeTab === 'all' ? deliveries : deliveries.filter(d => d.status === activeTab);
  return <div className="space-y-6" data-id="element-315">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-316">
        <StatsCard title="Total Deliveries" value="45,280" trend={8} icon={Navigation} color="blue" delay={0} data-id="element-317" />
        <StatsCard title="Active Now" value="342" trend={5} icon={Clock} color="green" delay={0.1} data-id="element-318" />
        <StatsCard title="Completed Today" value="1,247" trend={12} icon={CheckCircle} color="gold" delay={0.2} data-id="element-319" />
        <StatsCard title="Failed/Disputed" value="23" trend={-5} icon={AlertTriangle} color="purple" delay={0.3} data-id="element-320" />
      </div>

      <div className="flex border-b border-slate-200 overflow-x-auto" data-id="element-321">
        {['all', 'active', 'completed', 'failed', 'disputed', 'reschedule'].map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab ? 'border-famo-gold text-famo-navy' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-322">
              {tab.charAt(0).toUpperCase() + tab.slice(1).replace('_', ' ')}{' '}
              {tab === 'reschedule' ? 'Requests' : ''}
            </button>)}
      </div>

      {activeTab === 'reschedule' ? <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-323">
          {rescheduleRequests.map(req => <div key={req.id} className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-324">
              <div className="flex justify-between items-start mb-4" data-id="element-325">
                <div data-id="element-326">
                  <h3 className="font-bold text-slate-800" data-id="element-327">
                    Reschedule Request
                  </h3>
                  <p className="text-xs font-mono text-slate-500" data-id="element-328">
                    {req.deliveryId}
                  </p>
                </div>
                <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs font-bold" data-id="element-329">
                  Pending
                </span>
              </div>
              <div className="space-y-2 mb-6 text-sm" data-id="element-330">
                <div className="flex justify-between" data-id="element-331">
                  <span className="text-slate-500" data-id="element-332">Customer:</span>
                  <span className="font-medium text-slate-800" data-id="element-333">
                    {req.customer}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-334">
                  <span className="text-slate-500" data-id="element-335">Original Date:</span>
                  <span className="font-medium text-slate-800 line-through" data-id="element-336">
                    {req.originalDate}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-337">
                  <span className="text-slate-500" data-id="element-338">Requested Date:</span>
                  <span className="font-medium text-famo-gold" data-id="element-339">
                    {req.requestedDate}
                  </span>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg mt-2 text-slate-600 italic" data-id="element-340">
                  "{req.reason}"
                </div>
              </div>
              <div className="flex gap-3" data-id="element-341">
                <button className="flex-1 bg-green-50 text-green-700 hover:bg-green-100 py-2 rounded-lg font-medium transition-colors" data-id="element-342">
                  Approve
                </button>
                <button className="flex-1 bg-red-50 text-red-700 hover:bg-red-100 py-2 rounded-lg font-medium transition-colors" data-id="element-343">
                  Reject
                </button>
              </div>
            </div>)}
        </motion.div> : <motion.div key={activeTab} initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3
    }} data-id="element-344">
          <DataTable title="Deliveries" data={filteredData} columns={columns} searchPlaceholder="Search by ID, Customer or Rider..." actions={row => <>
                <button className="p-1.5 text-slate-500 hover:text-famo-navy hover:bg-slate-100 rounded transition-colors" title="View Details" data-id="element-346">
                  <Eye size={18} data-id="element-347" />
                </button>
                {(row.status === 'active' || row.status === 'in_transit' || row.status === 'picked_up') && <button className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors" title="Track Live" data-id="element-348">
                    <MapPin size={18} data-id="element-349" />
                  </button>}
              </>} data-id="element-345" />

          {/* Delivery Detail Panel */}
          <div className="mt-8 bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden" data-id="element-350">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50" data-id="element-351">
              <h3 className="text-lg font-bold text-slate-800" data-id="element-352">
                Delivery Detail —{' '}
                <span className="font-mono text-slate-500" data-id="element-353">#FAMO-9281</span>
              </h3>
              <StatusBadge status="active" data-id="element-354" />
            </div>

            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-355">
              {/* Left Column: Info & Timeline */}
              <div className="space-y-6" data-id="element-356">
                <div className="grid grid-cols-2 gap-4" data-id="element-357">
                  <div className="bg-slate-50 p-4 rounded-lg" data-id="element-358">
                    <p className="text-xs text-slate-500 uppercase font-bold mb-1" data-id="element-359">
                      Customer
                    </p>
                    <p className="font-medium text-slate-900" data-id="element-360">Alice Smith</p>
                    <p className="text-sm text-slate-500" data-id="element-361">+1 234 567 8910</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg" data-id="element-362">
                    <p className="text-xs text-slate-500 uppercase font-bold mb-1" data-id="element-363">
                      Rider
                    </p>
                    <p className="font-medium text-slate-900" data-id="element-364">John Doe</p>
                    <p className="text-sm text-slate-500" data-id="element-365">Bike: BK-1001</p>
                  </div>
                </div>

                <div className="border-l-2 border-slate-200 ml-3 pl-6 space-y-6 relative" data-id="element-366">
                  <div className="relative" data-id="element-367">
                    <div className="absolute -left-[31px] top-1 w-4 h-4 rounded-full bg-green-500 border-2 border-white shadow-sm" data-id="element-368"></div>
                    <p className="text-sm font-bold text-slate-900" data-id="element-369">Pickup</p>
                    <p className="text-sm text-slate-600" data-id="element-370">
                      123 Main St, Downtown
                    </p>
                    <p className="text-xs text-slate-400 mt-1" data-id="element-371">10:30 AM</p>
                  </div>
                  <div className="relative" data-id="element-372">
                    <div className="absolute -left-[31px] top-1 w-4 h-4 rounded-full bg-green-500 border-2 border-white shadow-sm" data-id="element-373"></div>
                    <p className="text-sm font-bold text-slate-900" data-id="element-374">
                      Picked Up
                    </p>
                    <p className="text-xs text-slate-400 mt-1" data-id="element-375">10:45 AM</p>
                  </div>
                  <div className="relative" data-id="element-376">
                    <div className="absolute -left-[31px] top-1 w-4 h-4 rounded-full bg-green-500 border-2 border-white shadow-sm animate-pulse" data-id="element-377"></div>
                    <p className="text-sm font-bold text-famo-gold" data-id="element-378">
                      In Transit
                    </p>
                    <p className="text-xs text-slate-400 mt-1" data-id="element-379">Now</p>
                  </div>
                  <div className="relative" data-id="element-380">
                    <div className="absolute -left-[31px] top-1 w-4 h-4 rounded-full bg-slate-300 border-2 border-white shadow-sm" data-id="element-381"></div>
                    <p className="text-sm font-bold text-slate-400" data-id="element-382">
                      Delivered
                    </p>
                    <p className="text-sm text-slate-400" data-id="element-383">
                      Terminal 3, LAX Airport
                    </p>
                    <p className="text-xs text-slate-400 mt-1" data-id="element-384">ETA: 11:15 AM</p>
                  </div>
                </div>
              </div>

              {/* Right Column: Pricing */}
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200" data-id="element-385">
                <h4 className="font-bold text-slate-800 mb-4 flex items-center gap-2" data-id="element-386">
                  <DollarSign size={18} className="text-famo-gold" data-id="element-387" />
                  Pricing Breakdown
                </h4>
                <div className="space-y-3 text-sm border-b border-slate-200 pb-4 mb-4" data-id="element-388">
                  <div className="flex justify-between" data-id="element-389">
                    <span className="text-slate-600" data-id="element-390">Base Fare</span>
                    <span className="font-medium text-slate-900" data-id="element-391">₦12.00</span>
                  </div>
                  <div className="flex justify-between" data-id="element-392">
                    <span className="text-slate-600" data-id="element-393">
                      Distance Fee (12.4 km × ₦2.50)
                    </span>
                    <span className="font-medium text-slate-900" data-id="element-394">₦31.00</span>
                  </div>
                  <div className="flex justify-between" data-id="element-395">
                    <span className="text-slate-600" data-id="element-396">Surge Pricing (1.2x)</span>
                    <span className="font-medium text-slate-900" data-id="element-397">₦5.16</span>
                  </div>
                  <div className="flex justify-between text-slate-500" data-id="element-398">
                    <span data-id="element-399">Platform Fee</span>
                    <span data-id="element-400">-₦4.82</span>
                  </div>
                </div>
                <div className="flex justify-between items-center mb-6" data-id="element-401">
                  <span className="font-bold text-slate-800" data-id="element-402">Total</span>
                  <span className="text-xl font-bold text-famo-gold" data-id="element-403">
                    ₦43.34
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm" data-id="element-404">
                  <span className="text-slate-600" data-id="element-405">
                    Payment Method:{' '}
                    <span className="font-medium text-slate-900" data-id="element-406">Wallet</span>
                  </span>
                  <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs font-bold" data-id="element-407">
                    PAID
                  </span>
                </div>
              </div>
            </div>

            {/* Actions Footer */}
            <div className="p-6 bg-slate-50 border-t border-slate-200 flex flex-wrap gap-4 items-center" data-id="element-408">
              <button className="bg-famo-gold text-white hover:opacity-90 rounded-lg px-4 py-2 text-sm font-medium transition-colors" data-id="element-409">
                Recalculate Price
              </button>

              <div className="flex items-center gap-2 border-l border-slate-300 pl-4 ml-2" data-id="element-410">
                <input type="text" placeholder="Reason for override..." className="bg-white border border-slate-300 rounded-lg text-sm px-3 py-2 w-48 focus:ring-2 focus:ring-red-200 focus:border-red-400 outline-none" data-id="element-411" />
                <button className="border border-red-200 text-red-600 hover:bg-red-50 rounded-lg px-4 py-2 text-sm font-medium transition-colors" data-id="element-412">
                  Override PIN
                </button>
              </div>

              <div className="flex-1" data-id="element-413"></div>

              <button className="border border-blue-200 text-blue-600 hover:bg-blue-50 rounded-lg px-4 py-2 text-sm font-medium transition-colors" data-id="element-414">
                Resolve Dispute
              </button>
              <button className="border border-red-200 text-red-600 hover:bg-red-50 rounded-lg px-4 py-2 text-sm font-medium transition-colors" data-id="element-415">
                Issue Refund
              </button>
            </div>
          </div>
        </motion.div>}
    </div>;
}